import React, { memo } from "react";
import { ReactSVG } from "react-svg";
import { UserSummaryStepProps } from "./types";
import Badge from "ui/components/Badge/Badge";

interface SummaryRowProps {
  label: string;
  value?: string | number | null;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-900 text-right">
      {value === null || value === undefined || value === "" ? "—" : value}
    </span>
  </div>
);

interface SummarySectionProps {
  icon: string;
  title: string;
  children: React.ReactNode;
}

const SummarySection: React.FC<SummarySectionProps> = ({ icon, title, children }) => (
  <div className="border-b border-slate-200 py-5 px-4 last:border-b-0">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
        <ReactSVG src={icon} className="w-4 h-4 stroke-slate-600" />
      </div>
        <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{title}</h3>
    </div>
    <div className="divide-y divide-slate-100">{children}</div>
  </div>
);

const UserSummaryStep: React.FC<UserSummaryStepProps> = ({
  formValues,
  roleOptions,
  officeOptions,
}) => {
  const fullName = [formValues.firstName, formValues.lastName]
    .filter(Boolean)
    .join(" ");

  const roleNames = (formValues.roles ?? []).map((role: any) => {
    const id = typeof role === "string" ? role : role?.id;
    const option = roleOptions.find((o) => o.value === id);
    return option?.text ?? (typeof role === "string" ? role : role?.name) ?? "";
  });

  const officeName = officeOptions.find(
    (o) => o.value === formValues.officeId
  )?.text;

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
          Pregled korisnika
        </h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Provjerite unesene podatke prije spremanja. Klikom na{" "}
          <strong>"Spremi"</strong> korisnik će biti pohranjen u sustav.
        </p>
        <div className="border border-slate-200">
          <SummarySection icon="/media/icons/contact.svg" title="Osobni podaci">
            <SummaryRow label="Ime i prezime" value={fullName} />
            <SummaryRow label="OIB" value={formValues.oib} />
          </SummarySection>

          <SummarySection icon="/media/icons/shield.svg" title="Korisnički podaci">
            <SummaryRow label="Korisničko ime" value={formValues.userName} />
            <div className="flex justify-between gap-4 py-1.5">
              <span className="text-sm text-slate-500">Uloge</span>
              <span className="flex flex-wrap justify-end gap-1.5">
                {roleNames.length > 0 ? (
                  roleNames.map((name, index) => (
                    <Badge key={index} text={name} variant="info" height="small" />
                  ))
                ) : (
                  <span className="text-sm font-medium text-slate-900">—</span>
                )}
              </span>
            </div>
            <SummaryRow label="Poslovnica" value={officeName} />
          </SummarySection>

          <SummarySection icon="/media/icons/phone.svg" title="Kontakt">
            <SummaryRow label="Mobitel" value={formValues.mobilePhoneNumber} />
            <SummaryRow label="Telefon" value={formValues.phoneNumber} />
            <SummaryRow label="Email" value={formValues.email} />
          </SummarySection>
        </div>
      </div>
    </div>
  );
};

export default memo(UserSummaryStep);
